import axios from 'axios';
import { GET, POST, JAVA_API_STATUS } from '../../utils/axios-interceptor';
import {
    MagicSignListRequest,
    MagicSignConfigRequest,
    SendSigningEmailRequest,
    SendReminderRequest,
    GlobalMagicSignListRequest,
    AssociateCompanyRequest,
    CreateMagicSignRequest,
} from './types';
import { MAGIC_SIGN_ENDPOINTS } from './constants';

export function fetchMagicSignList(payload: MagicSignListRequest) {
    return axios({
        url: MAGIC_SIGN_ENDPOINTS.LIST,
        method: POST,
        data: payload,
    });
}

/**
 * aica maps business-rule rejections to HTTP 200 with an error envelope, not an HTTP error
 * status — so we check responseData.responseCode here and throw, mirroring createMagicSignRecord.
 */
export async function updateMagicSignConfig(payload: MagicSignConfigRequest) {
    const res = await axios({
        url: MAGIC_SIGN_ENDPOINTS.UPDATE,
        method: POST,
        data: payload,
    });
    if (res?.data?.responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) {
        throw new Error(res?.data?.responseData?.responseMessage || 'Failed to update signing configuration.');
    }
    return res;
}

/**
 * aica maps business-rule rejections to HTTP 200 with an error envelope, not an HTTP error
 * status — so we check responseData.responseCode here and throw, mirroring createMagicSignRecord.
 */
export async function generateMagicSignLinks(payload: { id: string; origin?: string }) {
    const res = await axios({
        url: MAGIC_SIGN_ENDPOINTS.GENERATE_LINKS,
        method: POST,
        data: payload,
    });
    if (res?.data?.responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) {
        throw new Error(res?.data?.responseData?.responseMessage || 'Failed to generate signing links.');
    }
    return res;
}

/**
 * aica maps business-rule rejections to HTTP 200 with an error envelope, not an HTTP error
 * status — so we check responseData.responseCode here and throw, mirroring createMagicSignRecord.
 */
export async function cancelMagicSign(payload: { id: string }) {
    const res = await axios({
        url: MAGIC_SIGN_ENDPOINTS.CANCEL,
        method: POST,
        data: payload,
    });
    if (res?.data?.responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) {
        throw new Error(res?.data?.responseData?.responseMessage || 'Failed to cancel the signing request.');
    }
    return res;
}

export function fetchMagicSignAuditLog(id: string) {
    return axios({
        url: `${MAGIC_SIGN_ENDPOINTS.AUDIT}/${id}`,
        method: GET,
    });
}

export function downloadMagicSignDoc(id: string) {
    // Version-aware document bytes (signed copy when available, else stamped-on-the-fly).
    return axios({
        url: `${MAGIC_SIGN_ENDPOINTS.DOWNLOAD_DOC}/${id}`,
        method: GET,
        responseType: 'blob',
    });
}

export function fetchMagicSignConfigMeta(pnoId: string) {
    return axios({
        url: MAGIC_SIGN_ENDPOINTS.CONFIG_META,
        method: GET,
        params: { pnoId },
    });
}

// Phase 2 — signatory picker user list.
// Company users when orgId is provided; PNO (internal/admin) users when it is omitted —
// the backend routes on orgId's presence, so one function covers both call shapes.
export function fetchMagicSignUsers(params: { pnoId: string; orgId?: string; search?: string }) {
    return axios({
        url: MAGIC_SIGN_ENDPOINTS.USERS,
        method: GET,
        params,
    });
}

/**
 * aica maps business-rule rejections to HTTP 200 with an error envelope, not an HTTP error
 * status — so we check responseData.responseCode here and throw, mirroring createMagicSignRecord.
 */
export async function sendMagicSignEmail(payload: SendSigningEmailRequest) {
    const res = await axios({
        url: MAGIC_SIGN_ENDPOINTS.SEND_NOTIFICATION,
        method: POST,
        data: { ...payload, type: 'REVIEW' },
    });
    if (res?.data?.responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) {
        throw new Error(res?.data?.responseData?.responseMessage || 'Failed to send signing email.');
    }
    return res;
}

/**
 * aica maps business-rule rejections to HTTP 200 with an error envelope, not an HTTP error
 * status — so we check responseData.responseCode here and throw, mirroring createMagicSignRecord.
 */
export async function sendMagicSignReminder(payload: SendReminderRequest) {
    const res = await axios({
        url: MAGIC_SIGN_ENDPOINTS.SEND_NOTIFICATION,
        method: POST,
        data: { ...payload, type: 'REMINDER' },
    });
    if (res?.data?.responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) {
        throw new Error(res?.data?.responseData?.responseMessage || 'Failed to send reminder.');
    }
    return res;
}

// Phase 3 — global view + entry points

export function fetchGlobalMagicSignList(payload: GlobalMagicSignListRequest) {
    return axios({
        url: MAGIC_SIGN_ENDPOINTS.GLOBAL_LIST,
        method: POST,
        data: payload,
    });
}

/**
 * aica maps business-rule rejections to HTTP 200 with an error envelope, not an HTTP error
 * status — so we check responseData.responseCode here and throw, mirroring createMagicSignRecord.
 */
export async function associateMagicSignCompany(payload: AssociateCompanyRequest) {
    const res = await axios({
        url: MAGIC_SIGN_ENDPOINTS.UPDATE,
        method: POST,
        data: payload,
    });
    if (res?.data?.responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) {
        throw new Error(res?.data?.responseData?.responseMessage || 'Failed to associate company.');
    }
    return res;
}

/**
 * POST /magic/sign/create — P2 consolidated intake (RECUR-26495).
 * Replaces the deleted /upload and /sendForSigning endpoints.
 *
 * Precondition: the doc must already be registered in DocStore via the MU pipeline
 * (/magic/update/docMetaData) before this is called. For brand-new uploads from the
 * Magic Sign tab, run the full MU registration step first with source='MAGIC_SIGN'.
 * For existing docs (MU table "Add to Magic Sign", Data Room "Add to Magic Sign"),
 * the DocStore row already exists — call this directly.
 *
 * aica maps business-rule rejections (e.g. re-creating a signing record for a doc with
 * signatures in progress/completed) to HTTP 200 with an error envelope, not an HTTP error
 * status — so we check responseData.responseCode here and throw, this is where those
 * business errors become promise rejections for callers to catch.
 */
export async function createMagicSignRecord(payload: CreateMagicSignRequest) {
    const res = await axios({
        url: MAGIC_SIGN_ENDPOINTS.CREATE,
        method: POST,
        data: payload,
    });
    if (res?.data?.responseData?.responseCode !== JAVA_API_STATUS.SUCCESS) {
        throw new Error(res?.data?.responseData?.responseMessage || 'Failed to add document to Magic Sign.');
    }
    return res;
}
